import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';

function timeAgo(iso) {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function ModuleVideo({ config }) {
  const id = getYouTubeId(config.video_url);
  if (!id) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      {config.video_title && (
        <div style={{ fontSize: 14, fontWeight: 600, color: C.navy, marginBottom: 10 }}>
          {config.video_title}
        </div>
      )}
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 6, overflow: 'hidden', background: '#000' }}>
        <iframe
          src={`https://www.youtube.com/embed/${id}`}
          title={config.video_title || 'Module video'}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    </div>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────────

function MDBody({ text }) {
  if (!text) return null;
  return (
    <div style={{ fontSize: 14, color: C.navy, lineHeight: 1.65 }}>
      <ReactMarkdown
        components={{
          p:          ({ node, ...p }) => <p style={{ margin: '0 0 8px' }} {...p} />,
          strong:     ({ node, ...p }) => <strong {...p} />,
          em:         ({ node, ...p }) => <em {...p} />,
          h1:         ({ node, children }) => <div style={{ color: C.navy, fontSize: 16, fontWeight: 700, margin: '8px 0 4px' }}>{children}</div>,
          h2:         ({ node, children }) => <div style={{ color: C.navy, fontSize: 15, fontWeight: 700, margin: '8px 0 4px' }}>{children}</div>,
          h3:         ({ node, children }) => <div style={{ color: C.navy, fontSize: 14, fontWeight: 700, margin: '6px 0 3px' }}>{children}</div>,
          ul:         ({ node, ...p }) => <ul style={{ paddingLeft: 20, margin: '0 0 8px' }} {...p} />,
          ol:         ({ node, ...p }) => <ol style={{ paddingLeft: 20, margin: '0 0 8px' }} {...p} />,
          li:         ({ node, ...p }) => <li style={{ marginBottom: 2 }} {...p} />,
          blockquote: ({ node, ...p }) => (
            <blockquote style={{
              borderLeft: `3px solid ${C.rule}`, paddingLeft: 12,
              margin: '6px 0', color: C.muted, fontStyle: 'italic',
            }} {...p} />
          ),
          code: ({ node, inline, ...p }) => inline
            ? <code style={{ background: '#f0f0ee', padding: '1px 5px', borderRadius: 3, fontSize: 13, fontFamily: 'monospace' }} {...p} />
            : <pre style={{ background: '#f0f0ee', padding: '10px 14px', borderRadius: 4, overflowX: 'auto', fontSize: 13 }}><code {...p} /></pre>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ─── Markdown editor with toolbar ─────────────────────────────────────────────

function ToolbarBtn({ onClick, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title} style={{
      background: '#f0f0ee', border: `1px solid ${C.rule}`, borderRadius: 3,
      padding: '2px 8px', fontSize: 12, cursor: 'pointer',
      fontFamily: 'inherit', color: C.navy,
    }}>
      {children}
    </button>
  );
}

function MarkdownEditor({ value, onChange, placeholder, rows = 5 }) {
  const ref = useRef(null);

  function wrap(before, after = '') {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const sel   = value.slice(start, end);
    onChange(value.slice(0, start) + before + sel + after + value.slice(end));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + sel.length);
    }, 0);
  }

  function linePrefix(prefix) {
    const el = ref.current;
    if (!el) return;
    const start     = el.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    onChange(value.slice(0, lineStart) + prefix + value.slice(lineStart));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <ToolbarBtn onClick={() => wrap('**', '**')} title="Bold"><strong>B</strong></ToolbarBtn>
        <ToolbarBtn onClick={() => wrap('*', '*')} title="Italic"><em>I</em></ToolbarBtn>
        <ToolbarBtn onClick={() => linePrefix('- ')} title="Bullet list">• List</ToolbarBtn>
        <ToolbarBtn onClick={() => linePrefix('1. ')} title="Numbered list">1. List</ToolbarBtn>
        <ToolbarBtn onClick={() => wrap('> ')} title="Blockquote">❝ Quote</ToolbarBtn>
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        style={{ ...S.input, resize: 'vertical' }}
        placeholder={placeholder}
      />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
        **bold** · *italic* · ## heading · - bullet · &gt; quote
      </div>
    </div>
  );
}

// ─── New discussion form ───────────────────────────────────────────────────────

function NewDiscussionForm({ memberId, isAdmin, moduleMax, onPosted, onCancel }) {
  const [title, setTitle]         = useState('');
  const [body, setBody]           = useState('');
  const [moduleNum, setModuleNum] = useState(moduleMax || 1);
  const [posting, setPosting]     = useState(false);
  const [err, setErr]             = useState('');

  const moduleOptions = Array.from({ length: (moduleMax || 1) + 1 }, (_, i) => i + 1);

  async function handlePost() {
    setErr('');
    if (!title.trim()) { setErr('Title is required.'); return; }
    if (!body.trim())  { setErr('Post body is required.'); return; }
    setPosting(true);
    const { error } = await supabase.from('ceds_discussion_threads').insert({
      created_by:    memberId,
      title:         title.trim(),
      prompt:        body.trim(),
      module_number: moduleNum,
    });
    setPosting(false);
    if (error) { setErr(`Error: ${error.message}`); return; }
    onPosted();
  }

  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${C.gold}`, background: C.cream, marginBottom: 24 }}>
      <h3 style={{ color: C.navy, margin: '0 0 16px', fontSize: '1rem' }}>New Discussion Topic</h3>
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Module</label>
          <select value={moduleNum} onChange={e => setModuleNum(Number(e.target.value))}
            style={{ ...S.input, marginTop: 4 }}>
            {moduleOptions.map(n => (
              <option key={n} value={n}>Module {n}</option>
            ))}
          </select>
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={S.label}>Topic Title *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} style={S.input}
          placeholder="What do you want to discuss?" />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={S.label}>Opening Post *</label>
        <MarkdownEditor value={body} onChange={setBody} rows={6}
          placeholder="Share your thoughts, questions, or ideas…" />
      </div>
      {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={handlePost} disabled={posting}
          style={{ ...S.btn.primary, opacity: posting ? 0.65 : 1 }}>
          {posting ? 'Posting…' : 'Post Topic'}
        </button>
        <button onClick={onCancel} style={S.btn.secondary}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Reply form (used for both top-level and nested replies) ───────────────────

function ReplyForm({ discussionId, memberId, onPosted, parentReplyId = null, compact = false }) {
  const [body, setBody]       = useState('');
  const [posting, setPosting] = useState(false);
  const [err, setErr]         = useState('');

  async function handlePost() {
    setErr('');
    if (!body.trim()) { setErr('Reply cannot be empty.'); return; }
    setPosting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setErr('Your session has expired. Please refresh the page and sign in again.');
        return;
      }
      const res = await fetch('/api/post-reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          threadId: discussionId,
          content: body.trim(),
          ...(parentReplyId ? { parentReplyId } : {}),
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(result.error ?? 'Failed to post reply.'); setPosting(false); return; }
      setBody('');
      await onPosted();
    } catch (err) {
      setErr(`Network error: ${err.message}`);
    }
    setPosting(false);
  }

  return (
    <div style={{ marginTop: compact ? 8 : 16, paddingTop: compact ? 8 : 16, borderTop: compact ? 'none' : `1px solid ${C.rule}` }}>
      <MarkdownEditor value={body} onChange={setBody} rows={compact ? 3 : 4}
        placeholder={compact ? 'Write a reply…' : 'Add your reply…'} />
      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      <button onClick={handlePost} disabled={posting}
        style={{ ...S.btn.primary, marginTop: 8, fontSize: 13, padding: '7px 16px', opacity: posting ? 0.65 : 1 }}>
        {posting ? 'Posting…' : 'Post Reply'}
      </button>
    </div>
  );
}

// ─── Individual reply with nested replies ─────────────────────────────────────

function ReplyItem({ reply, nestedReplies, threadId, currentUser, isAdmin, onDeactivate, onRefresh }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const canDelete = isAdmin || reply.member_id === currentUser.id;

  return (
    <div>
      {/* Top-level reply */}
      <div style={{ background: C.cream, borderRadius: 4, padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: C.muted }}>
            <span style={{ fontWeight: 700, color: C.navy }}>
              {reply.ceds_council_members?.full_name}
            </span>
            {' · '}{timeAgo(reply.created_at)}
          </span>
          {canDelete && (
            <button onClick={() => onDeactivate(reply.id)} title="Remove reply"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, padding: 0, lineHeight: 1 }}>
              ✕
            </button>
          )}
        </div>
        <MDBody text={reply.content} />
        <button
          onClick={() => setShowReplyForm(v => !v)}
          style={{
            background: 'none', border: `1px solid ${C.rule}`, borderRadius: 3,
            padding: '3px 10px', fontSize: 12, color: C.muted, cursor: 'pointer',
            marginTop: 10, fontFamily: 'inherit',
          }}
        >
          {showReplyForm ? 'Cancel' : '↳ Reply'}
        </button>
      </div>

      {/* Nested replies (one level deep, no further nesting) */}
      {nestedReplies.length > 0 && (
        <div style={{ marginLeft: 24, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {nestedReplies.map(nested => {
            const canDeleteNested = isAdmin || nested.member_id === currentUser.id;
            return (
              <div key={nested.id} style={{
                background: '#eaeae6', borderRadius: 4, padding: '10px 14px',
                borderLeft: `3px solid ${C.rule}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>
                    <span style={{ fontWeight: 700, color: C.navy }}>
                      {nested.ceds_council_members?.full_name}
                    </span>
                    {' · '}{timeAgo(nested.created_at)}
                  </span>
                  {canDeleteNested && (
                    <button onClick={() => onDeactivate(nested.id)} title="Remove reply"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, padding: 0, lineHeight: 1 }}>
                      ✕
                    </button>
                  )}
                </div>
                <MDBody text={nested.content} />
              </div>
            );
          })}
        </div>
      )}

      {/* Inline reply form */}
      {showReplyForm && (
        <div style={{ marginLeft: 24, marginTop: 6 }}>
          <ReplyForm
            discussionId={threadId}
            parentReplyId={reply.id}
            memberId={currentUser.id}
            compact
            onPosted={async () => { await onRefresh(); setShowReplyForm(false); }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Discussion card ───────────────────────────────────────────────────────────

function DiscussionCard({ discussion, discussionNumber, replies, childMap, currentUser, isAdmin, onRefresh }) {
  const [open, setOpen]           = useState(false);
  const [editing, setEditing]     = useState(false);
  const [editTitle, setEditTitle] = useState(discussion.title);
  const [editBody, setEditBody]   = useState(discussion.prompt);
  const [saving, setSaving]       = useState(false);
  const canModerate = isAdmin || discussion.created_by === currentUser?.id;

  const totalReplies = replies.length + replies.reduce((sum, r) => sum + (childMap[r.id]?.length ?? 0), 0);

  async function deactivate() {
    const { error } = await supabase.rpc('deactivate_discussion', { discussion_id: discussion.id });
    if (error) { alert(`Could not remove topic: ${error.message}`); return; }
    onRefresh();
  }

  async function togglePin() {
    const { error } = await supabase.from('ceds_discussion_threads')
      .update({ is_pinned: !discussion.is_pinned })
      .eq('id', discussion.id);
    if (error) { alert(`Could not pin topic: ${error.message}`); return; }
    onRefresh();
  }

  async function deactivateReply(replyId) {
    const { error } = await supabase.rpc('deactivate_reply', { reply_id: replyId });
    if (error) { alert(`Could not remove reply: ${error.message}`); return; }
    onRefresh();
  }

  async function saveEdit() {
    if (!editTitle.trim()) { alert('Title is required.'); return; }
    setSaving(true);
    const { error } = await supabase.rpc('edit_discussion', {
      discussion_id: discussion.id,
      new_title:     editTitle.trim(),
      new_prompt:    editBody.trim(),
    });
    setSaving(false);
    if (error) { alert(`Could not save edit: ${error.message}`); return; }
    setEditing(false);
    onRefresh();
  }

  if (editing) {
    return (
      <div style={{ ...S.card, borderLeft: `4px solid ${C.gold}`, marginBottom: 12 }}>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Topic Title</label>
          <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ ...S.input, marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Post Body</label>
          <MarkdownEditor value={editBody} onChange={setEditBody} rows={6} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={saveEdit} disabled={saving}
            style={{ ...S.btn.primary, opacity: saving ? 0.65 : 1 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={() => { setEditing(false); setEditTitle(discussion.title); setEditBody(discussion.prompt); }}
            style={S.btn.secondary}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      ...S.card,
      borderLeft: discussion.is_pinned ? `4px solid ${C.gold}` : undefined,
      marginBottom: 12,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => setOpen(v => !v)}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>
              Discussion {discussionNumber}:
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{discussion.title}</span>
            {discussion.is_pinned && (
              <span style={{
                fontSize: 11, background: C.gold, color: C.navy,
                padding: '1px 8px', borderRadius: 10, fontWeight: 700, whiteSpace: 'nowrap',
              }}>
                📌 Pinned
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            <span style={{ fontWeight: 600, color: C.navy }}>{discussion.ceds_council_members?.full_name}</span>
            {' · '}{timeAgo(discussion.created_at)}
            {totalReplies > 0 && (
              <>
                {' · '}{totalReplies} {totalReplies === 1 ? 'reply' : 'replies'}
                {' · last activity '}{timeAgo(discussion.updated_at)}
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          {isAdmin && (
            <button onClick={togglePin} title={discussion.is_pinned ? 'Unpin' : 'Pin to top'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 4px', opacity: discussion.is_pinned ? 1 : 0.4 }}>
              📌
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setEditing(true)} title="Edit topic"
              style={{ ...S.btn.secondary, fontSize: 12, padding: '4px 10px' }}>
              Edit
            </button>
          )}
          {canModerate && (
            <button onClick={deactivate} title="Remove topic"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: C.muted, padding: '2px 4px', lineHeight: 1 }}>
              ✕
            </button>
          )}
          <button onClick={() => setOpen(v => !v)}
            style={{ ...S.btn.secondary, fontSize: 12, padding: '5px 14px' }}>
            {open ? 'Close' : totalReplies > 0 ? `${totalReplies} ${totalReplies === 1 ? 'Reply' : 'Replies'} ↓` : 'Open →'}
          </button>
        </div>
      </div>

      {/* Expanded thread */}
      {open && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.rule}` }}>
          <MDBody text={discussion.prompt} />

          {replies.length > 0 && (
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {replies.map(reply => (
                <ReplyItem
                  key={reply.id}
                  reply={reply}
                  nestedReplies={childMap[reply.id] ?? []}
                  threadId={discussion.id}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  onDeactivate={deactivateReply}
                  onRefresh={onRefresh}
                />
              ))}
            </div>
          )}

          <ReplyForm
            discussionId={discussion.id}
            memberId={currentUser.id}
            onPosted={onRefresh}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function DiscussionsPage() {
  const [discussions, setDiscussions] = useState([]);
  const [replyMap, setReplyMap]       = useState({});
  const [childMap, setChildMap]       = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showNew, setShowNew]         = useState(false);
  const [videoMap, setVideoMap]       = useState({});

  const load = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const user = session.user;

    const [{ data: member }, { data: allMembers }, { data: discs }, { data: replies }, { data: videos }] = await Promise.all([
      supabase
        .from('ceds_council_members')
        .select('id, role, full_name')
        .eq('id', user.id)
        .maybeSingle(),
      supabase
        .from('ceds_council_members')
        .select('id, full_name'),
      supabase
        .from('ceds_discussion_threads')
        .select('*')
        .eq('is_active', true)
        .order('module_number', { ascending: true })
        .order('sort_order', { ascending: true }),
      supabase
        .from('ceds_discussion_replies')
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: true }),
      supabase
        .from('ceds_module_config')
        .select('module_number, video_url, video_title'),
    ]);

    const memberMap = {};
    (allMembers ?? []).forEach(m => { memberMap[m.id] = m; });

    const threadsWithMembers = (discs ?? []).map(d => ({
      ...d,
      ceds_council_members: memberMap[d.created_by] ?? null,
    }));

    const repliesWithMembers = (replies ?? []).map(r => ({
      ...r,
      ceds_council_members: memberMap[r.member_id ?? r.created_by] ?? null,
    }));

    // Separate top-level replies from nested replies
    const topMap = {};   // thread_id -> top-level replies
    const kidMap = {};   // parent_reply_id -> nested replies

    repliesWithMembers.forEach(r => {
      if (r.parent_reply_id) {
        if (!kidMap[r.parent_reply_id]) kidMap[r.parent_reply_id] = [];
        kidMap[r.parent_reply_id].push(r);
      } else {
        if (!topMap[r.thread_id]) topMap[r.thread_id] = [];
        topMap[r.thread_id].push(r);
      }
    });

    const vm = {};
    (videos ?? []).forEach(v => { if (v.video_url) vm[v.module_number] = v; });
    setVideoMap(vm);

    setCurrentUser(member);
    setDiscussions(threadsWithMembers);
    setReplyMap(topMap);
    setChildMap(kidMap);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ color: C.muted, paddingTop: 60, textAlign: 'center' }}>Loading discussions…</div>
  );

  if (!currentUser) return (
    <div style={{
      color: C.red, paddingTop: 60, textAlign: 'center',
      fontSize: 14, maxWidth: 400, margin: '0 auto', lineHeight: 1.6,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Account not recognized.</div>
      Your login is valid but your council member record wasn't found.
      Please contact the portal administrator for assistance.
    </div>
  );

  const isAdmin = currentUser?.role === 'padd_admin';

  const moduleNums = [...new Set(discussions.map(d => d.module_number ?? 1))].sort((a, b) => a - b);
  const maxModule  = moduleNums.length > 0 ? Math.max(...moduleNums) : 0;
  const modulesToShow = moduleNums.length > 0 ? [...moduleNums, maxModule + 1] : [1];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ color: C.navy, margin: '0 0 4px', fontSize: '1.5rem' }}>Discussions</h2>
          <p style={{ color: C.muted, margin: 0, fontSize: 14 }}>
            Post topics, share ideas, and continue the conversation between meetings.
          </p>
        </div>
        {!showNew && (
          <button onClick={() => setShowNew(true)}
            style={{ ...S.btn.gold, whiteSpace: 'nowrap', marginLeft: 16 }}>
            + New Topic
          </button>
        )}
      </div>

      {showNew && (
        <NewDiscussionForm
          memberId={currentUser.id}
          isAdmin={isAdmin}
          moduleMax={maxModule || 1}
          onPosted={async () => { await load(); setShowNew(false); }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {modulesToShow.map((modNum, modIdx) => {
        const modDiscs = discussions.filter(d => (d.module_number ?? 1) === modNum);
        return (
          <div key={modNum} style={{ marginBottom: 32 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 16, marginTop: modIdx > 0 ? 8 : 0,
              paddingBottom: 10, borderBottom: `2px solid ${C.navy}`,
            }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: C.navy, letterSpacing: '0.01em' }}>
                Module {modNum}
              </span>
              {modDiscs.length > 0 && (
                <span style={{ fontSize: 13, color: C.muted }}>
                  {modDiscs.length} discussion{modDiscs.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {videoMap[modNum] && <ModuleVideo config={videoMap[modNum]} />}

            {modDiscs.length === 0 ? (
              <div style={{ ...S.card, color: C.muted, padding: '20px 24px', fontSize: 14, fontStyle: 'italic' }}>
                Discussions for this module will be posted here when they're ready.
              </div>
            ) : (
              modDiscs.map((d, idx) => (
                <DiscussionCard
                  key={d.id}
                  discussion={d}
                  discussionNumber={idx + 1}
                  replies={replyMap[d.id] ?? []}
                  childMap={childMap}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  onRefresh={load}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
